import { engineComponent } from "neo-common";
import { Communication, Platforms } from "neo-games";
import { FrameTimerEventEmitter, CreateFrameTimer, FrameTimerEvents } from "../../Utils/FrameTimer";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { UserControls } from "../Common/UserControls/UserControls";
import { HtmlController } from "../../HTMLComponents/HtmlController";
import { GameManager } from "../../Managers/GameManager";
import { GameData } from "../Common/Datas/GameData";
import { GameActions } from "../Common/Actions/GameActions";
import { AppContext } from "../../../Common/AppContext";
import { ModuleManager } from "../../Managers/ModuleManager";
import { DataManager } from "../../Managers/DataManager";
import { LimitDialog } from "../Common/Dialogs/LimitDialog";
import { FreespinsController } from "../../GameComponents/Freespins/FreespinsController";
import { FreespinsActions } from "../Common/Actions/FreespinsActions";
import { FreeRoundBonus } from "../Common/Bonuses/FreeRoundBonus";
import { BaseActions } from "../Common/Actions/BaseActions";

var GameModules = Communication.Game.GameModules;
var WinType = Communication.Game.WinType;

@engineComponent(ComponentTypes.HtmlElement)
export class DesktopFreespinsUserControls extends UserControls {
    protected _controller: FreespinsController;

    private _userControls: HTMLElement;
    private _userControls_supreme: HTMLElement;
    private _afterWinDelay: FrameTimerEventEmitter;
    private _autoplayDelay: FrameTimerEventEmitter;
    private _retriggerDelay: FrameTimerEventEmitter;
    private _respinDelay: FrameTimerEventEmitter;

    private _wasCalclulated: boolean = true;
    private _machineStopped: boolean = true;

    constructor() {
        super("freespins");
    }

    create(node: Element): void {
        super.create(node);

        this.registerActions();

        this._afterWinDelay = CreateFrameTimer("afterWinDelay");
        this._afterWinDelay.on(FrameTimerEvents.Complete, this.prepareNewGameRound.bind(this));

        this._autoplayDelay = CreateFrameTimer("autoplayDelay");
        this._autoplayDelay.on(FrameTimerEvents.Complete, this.autoplayDelayed.bind(this));

        this._retriggerDelay = CreateFrameTimer("retriggerDelay");
        this._retriggerDelay.on(FrameTimerEvents.Complete, this.retriggerDelayed.bind(this));

        this._respinDelay = CreateFrameTimer("respinDelay");
        this._respinDelay.on(FrameTimerEvents.Complete, this.respin.bind(this));

        this._userControls = document.getElementById("freespinsUserControls");
    }

    addController(controller: FreespinsController): void {
        this._controller = controller;
        this._controller.on(FreespinsController.StartReceived, this.startReceived.bind(this));
        this._controller.on(FreespinsController.SpinReceived, this.spinReceived.bind(this));
        this._controller.on(FreespinsController.EndReceived, this.endReceived.bind(this));
        this._controller.on(FreespinsController.MachineStopped, this.machineStopped.bind(this));
        this._controller.on(FreespinsController.WinCalculationFinished, this.winCalculationFinished.bind(this));
        this._controller.on(FreespinsController.AllWinsFinished, this.allWinsFinished.bind(this));
        this._controller.on(FreespinsController.IntroFinished, this.introFinished.bind(this));
        this._controller.on(FreespinsController.OutroFinished, this.outroFinished.bind(this));
    }

    registerActions(): void {
        this.actions.registerActions({
            [FreespinsActions.ActionStart]: false,
            [FreespinsActions.ActionBack]: false,
            [FreespinsActions.ActionAutoplay]: false,
            [FreespinsActions.ActionAutoplayStop]: false,
            [FreespinsActions.ActionSpin]: false,
            [FreespinsActions.ActionSpinStop]: false,
            [FreespinsActions.ActionSkipWin]: false,
        });
    }

    initReceived(): void {
        this._userControls_supreme = document.getElementById("userControls_supreme");

        super.initReceived();

        this._controller.initReceived();

        if (this._userControls != null) {
            this._userControls.style.display = "none";
        }
        if (this._userControls != null && !this._userControls.classList.contains(HtmlController.DisabledClass)) {
            this._userControls.classList.add(HtmlController.DisabledClass);
        }

        if (this._userControls_supreme != null) {
            this._userControls_supreme.style.display = "none";
        }
        if (
            this._userControls_supreme != null &&
            !this._userControls_supreme.classList.contains(HtmlController.DisabledClass)
        ) {
            this._userControls_supreme.classList.add(HtmlController.DisabledClass);
        }
    }

    restoreGameRound(): void {
        this.prepareNewGameRound();
    }

    startReceived(): void {
        ModuleManager.outroModule(GameModules.Pick);
    }

    spinReceived(): void {
        if (DataManager.quickspinEnabled()) {
            // auto quick stop
            this.actions.changeActionState(FreespinsActions.ActionSpinStop, false);
            HtmlController.updateActionElements(this.getAllActions());
            HtmlController.dispatchUiAction("");
            this._controller.stop();
        } else {
            this.actions.changeActionState(
                FreespinsActions.ActionSpinStop,
                AppContext.gameConfiguration.GameEnableReelsStop,
            );
            HtmlController.updateActionElements(this.getAllActions());
        }
    }

    endReceived(): void {
        ModuleManager.outroModule(GameModules.Freespins);
    }

    dispatchAction(action: string): void {
        if (action === GameActions.ActionSpinOrStop) {
            if (this.actions.getActionState(FreespinsActions.ActionStart)) {
                action = FreespinsActions.ActionStart;
            } else if (this.actions.getActionState(FreespinsActions.ActionBack)) {
                action = FreespinsActions.ActionBack;
            } else if (this.actions.getActionState(FreespinsActions.ActionSpin)) {
                action = FreespinsActions.ActionSpin;
            } else if (this.actions.getActionState(FreespinsActions.ActionSpinStop)) {
                action = FreespinsActions.ActionSpinStop;
            } else {
                return;
            }
        }

        if (this.actions.getActionState(action)) {
            super.dispatchAction(action);

            switch (action) {
                case FreespinsActions.ActionStart:
                    this.start();
                    break;
                case FreespinsActions.ActionBack:
                    this.back();
                    break;
                case FreespinsActions.ActionSpin:
                    this.spin();
                    break;
                case FreespinsActions.ActionSpinStop:
                    this.stop();
                    break;
                case FreespinsActions.ActionSkipWin:
                    this.skipWin();
                    break;
                case FreespinsActions.ActionAutoplay:
                    this.autoplay();
                    break;
                case FreespinsActions.ActionAutoplayStop:
                    this.autoplayStop();
                    break;
            }
        }
    }

    start(): void {
        this.actions.disableAllActions();
        HtmlController.updateActionElements(this.getAllActions());
        this._controller.start();
    }

    spin(): void {
        this._machineStopped = false;
        if (!this._wasCalclulated) {
            if (GameManager.freespinsResponse != null && GameManager.freespinsResponse.currentWin > 0) {
                Platforms.Platforms.win(GameManager.freespinsResponse.currentWin);
            }
        }

        DataManager.updateDataElement(
            GameData.DataFreeSpinsRemainingCount,
            (GameManager.freespinsResponse.freeSpins.remainingCount - 1).toString(),
        );

        this.actions.disableAllActions();
        if (DataManager.Autoplay) {
            this.actions.changeActionState(
                FreespinsActions.ActionAutoplayStop,
                AppContext.gameConfiguration.AutoplayModeEnabled,
            );
        }
        HtmlController.dispatchUiAction("");
        DataManager.updateDataElement(GameData.DataTotalWin);
        HtmlController.updateActionElements(this.getAllActions());
        this._controller.spin();

        Platforms.Platforms.spinStarted();
    }

    respin(): void {
        this.actions.disableAllActions();
        if (DataManager.Autoplay) {
            this.actions.changeActionState(FreespinsActions.ActionAutoplayStop, true);
        }
        HtmlController.dispatchUiAction("");
        HtmlController.updateActionElements(this.getAllActions());
        this._controller.respin();

        Platforms.Platforms.spinStarted();
    }

    back(): void {
        this.disable();
        ModuleManager.endFreespins();
    }

    stop(): void {
        this.actions.changeActionState(FreespinsActions.ActionSpinStop, false);
        HtmlController.updateActionElements(this.getAllActions());
        HtmlController.dispatchUiAction("");
        DataManager.quickspinStopped();
        this._controller.stop();
    }

    end(): void {
        if (GameManager.freespinsResponse != null && GameManager.freespinsResponse.totalWin > 0) {
            Platforms.Platforms.win(GameManager.freespinsResponse.totalWin, true);
        }

        this._controller.end();
    }

    autoplayDelayed(): void {
        this._autoplayDelay.stop();
        if (DataManager.Autoplay) {
            this.autoplay();
        } else {
            this.prepareNewGameRound();
        }
    }

    prepareSkipWin(): void {
        if (
            GameManager.freespinsResponse.currentWin === 0 ||
            !AppContext.gameConfiguration.AutoplaySkipWinAnimationsEnabled
        ) {
            return;
        }

        this.actions.changeActionState(FreespinsActions.ActionSkipWin, true);
        HtmlController.updateActionElement(FreespinsActions.ActionSkipWin, true);
    }

    skipWin(): void {
        this.actions.changeActionState(FreespinsActions.ActionSkipWin, false);
        HtmlController.updateActionElement(FreespinsActions.ActionSkipWin, false);

        this._autoplayDelay.stop();

        if (DataManager.Autoplay) {
            this.autoplay();
        } else {
            this.spin();
        }
    }

    autoplay(): void {
        this._afterWinDelay.stop();

        if (DataManager.AutoplayPaused) {
            this.actions.changeActionState(
                FreespinsActions.ActionAutoplayStop,
                AppContext.gameConfiguration.AutoplayModeEnabled,
            );
            HtmlController.updateActionElement(
                FreespinsActions.ActionAutoplayStop,
                AppContext.gameConfiguration.AutoplayModeEnabled,
            );
            return;
        }

        if (DataManager.AutoplayCount === "∞") {
            DataManager.Autoplay = true;
            DataManager.updateDataElement(GameData.DataAutoplayCount, DataManager.AutoplayCount);
            this.spin();
        } else {
            DataManager.AutoplayCount = (parseInt(DataManager.AutoplayCount) - 1).toString();
            if (parseInt(DataManager.AutoplayCount) >= 0) {
                DataManager.Autoplay = true;
                DataManager.updateDataElement(GameData.DataAutoplayCount, DataManager.AutoplayCount);
                this.spin();
            } else {
                DataManager.Autoplay = false;
                this.prepareNewGameRound();
            }
        }
    }

    autoplayStop(): void {
        DataManager.Autoplay = false;
        this.actions.changeActionState(FreespinsActions.ActionAutoplayStop, false);
        HtmlController.updateActionElement(FreespinsActions.ActionAutoplayStop, false);
    }

    machineStopped(): void {
        this._machineStopped = true;
        Platforms.Platforms.spinEnded();

        if (GameManager.freespinsResponse.currentWin === 0) {
            this._wasCalclulated = true;
        } else {
            this._wasCalclulated = false;
        }

        if (this.retrigger()) {
            this.actions.changeActionState(FreespinsActions.ActionSpinStop, false);
            HtmlController.updateActionElement(FreespinsActions.ActionSpinStop, false);
            return;
        }

        if (this.isLastFreespin()) {
            DataManager.Autoplay = false;
            this.actions.disableAllActions();
            if (GameManager.freespinsResponse.currentWin === 0) {
                if (GameManager.freespinsResponse.totalWin != null) {
                    DataManager.updateGameDataFromModule(
                        GameData.DataFreeSpinTotalWin,
                        GameManager.freespinsResponse.totalWin.toString(),
                    );
                }
                DataManager.updateDataElement(GameData.DataFreeSpinTotalWin);
                this.actions.changeActionState(FreespinsActions.ActionBack, true);
            }
            HtmlController.updateActionElements(this.getAllActions());
            return;
        } else if (this.isRespin()) {
            this.actions.changeActionState(FreespinsActions.ActionSpinStop, false);

            HtmlController.updateActionElements(this.getAllActions());

            this._respinDelay.start(50);
            return;
        }

        this.actions.changeActionState(FreespinsActions.ActionSpinStop, false);

        if (GameManager.freespinsResponse.currentWin > 0) {
            this._afterWinDelay.start(30);
        } else {
            this.prepareNewGameRound();
        }

        HtmlController.updateActionElements(this.getAllActions());

        if (DataManager.Autoplay && GameManager.freespinsResponse.currentWin === 0) {
            this._autoplayDelay.start(15);
        }
    }

    winCalculationFinished(): void {
        if (this.retrigger()) {
            return;
        }

        this._afterWinDelay.stop();

        DataManager.updateDataElement(GameData.DataTotalWin);

        if (GameManager.freespinsResponse.totalWin != null) {
            DataManager.updateGameDataFromModule(
                GameData.DataFreeSpinTotalWin,
                GameManager.freespinsResponse.totalWin.toString(),
            );
        }
        DataManager.updateDataElement(GameData.DataFreeSpinTotalWin);

        if (this.isLastFreespin()) {
            DataManager.Autoplay = false;
            this.actions.disableAllActions();
            HtmlController.updateActionElements(this.getAllActions());
            return;
        }

        this.prepareNewGameRound();
    }

    allWinsFinished(): void {
        if (
            GameManager.freespinsResponse != null &&
            GameManager.freespinsResponse.currentWin > 0 &&
            !this._wasCalclulated
        ) {
            Platforms.Platforms.win(GameManager.freespinsResponse.currentWin);
        }

        this._wasCalclulated = true;

        if (this.retrigger(true)) {
            this._retriggerDelay.start(60);
            return;
        }

        this._afterWinDelay.stop();

        if (this.isLastFreespin()) {
            DataManager.Autoplay = false;
            this.actions.disableAllActions();
            this.actions.changeActionState(FreespinsActions.ActionBack, true);
            HtmlController.updateActionElements(this.getAllActions());
            return;
        }

        this.prepareNewGameRound();
        if (DataManager.Autoplay) {
            this.autoplay();
        }
    }

    retriggerDelayed(): void {
        this._afterWinDelay.stop();

        if (this.isLastFreespin()) {
            DataManager.Autoplay = false;
            this.actions.disableAllActions();
            this.actions.changeActionState(FreespinsActions.ActionBack, true);
            HtmlController.updateActionElements(this.getAllActions());
            return;
        }

        this.prepareNewGameRound();
        if (DataManager.Autoplay) {
            this.autoplay();
        }
    }

    initIntro(): void {
        FreeRoundBonus.disableUi();
        DataManager.updateDataElement(
            GameData.DataFreeSpinsRemainingCount,
            GameManager.freespinsResponse.freeSpins.remainingCount.toString(),
        );

        DataManager.updateDataElement(GameData.DataTotalWin);

        if (GameManager.freespinsResponse.totalWin != null) {
            DataManager.updateGameDataFromModule(
                GameData.DataFreeSpinTotalWin,
                GameManager.freespinsResponse.totalWin.toString(),
            );
        }
        DataManager.updateDataElement(GameData.DataFreeSpinTotalWin);

        if (this._userControls != null) {
            this._userControls.style.display = "block";
        }
        if (this._userControls != null && this._userControls.classList.contains(HtmlController.DisabledClass)) {
            this._userControls.classList.remove(HtmlController.DisabledClass);
        }

        if (this._userControls_supreme != null) {
            this._userControls_supreme.style.display = "block";
        }
        if (
            this._userControls_supreme != null &&
            this._userControls_supreme.classList.contains(HtmlController.DisabledClass)
        ) {
            this._userControls_supreme.classList.remove(HtmlController.DisabledClass);
        }

        this.retrigger(true);

        super.initIntro();
        this._controller.initIntro();
        Platforms.Platforms.featureInit({ type: Communication.Game.GameModules.Freespins });

        if (this.isLastFreespin()) {
            this.actions.disableAllActions();
            this.actions.changeActionState(FreespinsActions.ActionBack, true);
            HtmlController.updateActionElements(this.getAllActions());
            return;
        } else if (this.isRespin()) {
            this._respinDelay.start(0);
            return;
        }

        this.prepareNewGameRound();
    }

    intro(long: boolean = false): void {
        FreeRoundBonus.disableUi();
        DataManager.updateDataElement(
            GameData.DataFreeSpinsRemainingCount,
            GameManager.freespinsResponse.freeSpins.remainingCount.toString(),
        );

        this._controller.intro(long);

        if (this._userControls != null) {
            this._userControls.style.display = "block";
        }
        if (this._userControls != null && this._userControls.classList.contains(HtmlController.DisabledClass)) {
            this._userControls.offsetWidth;
            this._userControls.classList.remove(HtmlController.DisabledClass);
        }

        if (this._userControls_supreme != null) {
            this._userControls_supreme.style.display = "block";
        }
        if (
            this._userControls_supreme != null &&
            this._userControls_supreme.classList.contains(HtmlController.DisabledClass)
        ) {
            this._userControls_supreme.offsetWidth;
            this._userControls_supreme.classList.remove(HtmlController.DisabledClass);
        }
    }

    introFinished(): void {
        this.prepareNewGameRound();
    }

    outro(): void {
        DataManager.Autoplay = false;

        this._controller.outro();

        const baseUserControls = document.getElementById("userControls");

        if (baseUserControls != null) {
            baseUserControls.style.display = "none";
        }
        if (baseUserControls != null && !baseUserControls.classList.contains(HtmlController.DisabledClass)) {
            baseUserControls.classList.add(HtmlController.DisabledClass);
        }

        if (this._userControls != null) {
            this._userControls.style.display = "none";
        }
        if (this._userControls != null && !this._userControls.classList.contains(HtmlController.DisabledClass)) {
            this._userControls.classList.add(HtmlController.DisabledClass);
        }

        if (this._userControls_supreme != null) {
            this._userControls_supreme.style.display = "none";
        }
        if (
            this._userControls_supreme != null &&
            !this._userControls_supreme.classList.contains(HtmlController.DisabledClass)
        ) {
            this._userControls_supreme.classList.add(HtmlController.DisabledClass);
        }
    }

    outroFinished(): void {
        ModuleManager.introModule(GameModules.Base, true);

        if (this._userControls != null) {
            this._userControls.style.display = "none";
        }
        if (this._userControls != null && !this._userControls.classList.contains(HtmlController.DisabledClass)) {
            this._userControls.classList.add(HtmlController.DisabledClass);
        }

        if (this._userControls_supreme != null) {
            this._userControls_supreme.style.display = "none";
        }
        if (
            this._userControls_supreme != null &&
            !this._userControls_supreme.classList.contains(HtmlController.DisabledClass)
        ) {
            this._userControls_supreme.classList.add(HtmlController.DisabledClass);
        }
    }

    prepareNewGameRound(): void {
        this._afterWinDelay.stop();

        if (GameManager.freespinsResponse.totalWin != null) {
            DataManager.updateGameDataFromModule(
                GameData.DataFreeSpinTotalWin,
                GameManager.freespinsResponse.totalWin.toString(),
            );
        }
        DataManager.updateDataElement(GameData.DataFreeSpinTotalWin);

        if (DataManager.GameLimit) {
            DataManager.Autoplay = false;
            this.limit();
        } else {
            if (!DataManager.Autoplay) {
                this.actions.changeActionStates({
                    [FreespinsActions.ActionStart]: false,
                    [FreespinsActions.ActionBack]: false,
                    [FreespinsActions.ActionAutoplay]:
                        !DataManager.Autoplay && AppContext.gameConfiguration.AutoplayModeEnabled,
                    [FreespinsActions.ActionAutoplayStop]:
                        DataManager.Autoplay && AppContext.gameConfiguration.AutoplayModeEnabled,
                    [FreespinsActions.ActionSpin]: true,
                    [FreespinsActions.ActionSpinStop]: false,
                    [FreespinsActions.ActionSkipWin]: false,
                });
            } else {
                this.prepareSkipWin();
            }
        }

        HtmlController.updateActionElements(this.getAllActions());
    }

    limit(): void {
        LimitDialog.openDialog();

        this.actions.changeActionStates({
            [FreespinsActions.ActionStart]: false,
            [FreespinsActions.ActionBack]: false,
            [FreespinsActions.ActionAutoplay]: false,
            [FreespinsActions.ActionAutoplayStop]: false,
            [FreespinsActions.ActionSpin]: false,
            [FreespinsActions.ActionSpinStop]: false,
            [FreespinsActions.ActionSkipWin]: false,
        });
    }

    enable(): void {
        /*DataManager.updateDataElement(GameData.DataFreeSpinsRemainingCount, (GameManager.freespinsResponse.freeSpins.remainingCount).toString());
        DataManager.updateDataElement(GameData.DataFreeSpinsCount, (GameManager.freespinsResponse.freeSpins.totalCount).toString());
        this.actions.changeActionState(FreespinsActions.ActionStart, true);
        HtmlController.updateActionElements(this.getAllActions());
        this._controller.enable();*/

        //automatically run start
        //needs to be wrapped in timeout, to release sendRequest value
        setTimeout(() => {
            this.start();
        }, 1);
    }

    restoreActions() {
        super.restoreActions();

        if (
            DataManager.Autoplay &&
            this._machineStopped &&
            this.actions.getActionState(FreespinsActions.ActionAutoplayStop)
        ) {
            this._autoplayDelay.stop();
            this._autoplayDelay.start(30);
        }
    }

    disable(): void {
        this.actions.disableAllActions();
        HtmlController.updateActionElements(this.getAllActions());
    }

    private retrigger(updateCount: boolean = false): boolean {
        let wasRetrigger: boolean = false;

        if (GameManager.freespinsResponse.wins != null && GameManager.freespinsResponse.wins.length > 0) {
            for (let i: number = 0; i < GameManager.freespinsResponse.wins.length; i++) {
                if (GameManager.freespinsResponse.wins[i].type === WinType.freeSpins) {
                    if (updateCount) {
                        DataManager.updateDataElement(
                            GameData.DataFreeSpinsRemainingCount,
                            GameManager.freespinsResponse.freeSpins.remainingCount.toString(),
                        );
                    }
                    let count: number = GameManager.freespinsResponse.wins[i].customData["count"];
                    if (count != null) {
                        DataManager.updateDataElement(GameData.DataFreeSpinsCount, count.toString());
                        DataManager.updateDataElement(GameData.DataFreeSpinsWonCount, count.toString());
                    }
                    wasRetrigger = true;
                }
            }
        }

        return wasRetrigger && !this.isLastFreespin();
    }

    private isLastFreespin(): boolean {
        if (DataManager.GameLimit) {
            this.limit();
            return true;
        }

        if (
            GameManager.freespinsResponse.freeSpins != null &&
            GameManager.freespinsResponse.freeSpins.remainingCount != null &&
            GameManager.freespinsResponse.freeSpins.remainingCount === 0 &&
            !this.isRespin()
        ) {
            return true;
        }

        return false;
    }

    private isRespin(): boolean {
        return (
            GameManager.freespinsResponse != null &&
            GameManager.freespinsResponse.respin != null &&
            GameManager.freespinsResponse.respin !== 0
        );
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/Channels/Desktop/DesktopFreespinsUserControls.ts
 **/